import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Activity, DollarSign, Zap, Server } from 'lucide-react';
import { useProject } from '../hooks/useProjects';
import { useProjectStats } from '../hooks/useAnalytics';
import StatCard from '../components/StatCard';

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatCost(n: number | string): string {
  const v = Number(n);
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
}

function CostByDayChart({ data }: { data: { date: string; cost: number; tokens: number }[] }) {
  const maxCost = Math.max(...data.map((d) => d.cost), 0.01);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <h3 className="mb-4 text-sm font-semibold text-slate-900">Cost by Day (last 30 days)</h3>
      {data.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400">No cost data yet</div>
      ) : (
        <div className="flex items-end gap-1" style={{ height: 200 }}>
          {data.map((d) => {
            const heightPct = Math.max((d.cost / maxCost) * 100, 2);
            const dateLabel = new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return (
              <div
                key={d.date}
                className="group relative flex flex-1 flex-col items-center justify-end"
                style={{ height: '100%' }}
              >
                <div className="pointer-events-none absolute -top-10 z-10 hidden rounded bg-slate-800 px-2 py-1 text-xs text-white shadow-lg group-hover:block">
                  {formatCost(d.cost)} / {formatTokens(d.tokens)} tokens
                </div>
                <div
                  className="w-full rounded-t bg-blue-500 transition-all hover:bg-blue-600"
                  style={{ height: `${heightPct}%`, minHeight: 2 }}
                />
                {data.length <= 15 && (
                  <span className="mt-1 text-[10px] text-slate-400">{dateLabel}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusBreakdown({ data }: { data: Record<string, number> }) {
  const entries = Object.entries(data).filter(([, count]) => count > 0);
  const total = entries.reduce((sum, [, count]) => sum + count, 0);

  const colorMap: Record<string, string> = {
    completed: 'bg-green-500',
    failed: 'bg-red-500',
    stopped: 'bg-slate-400',
    paused: 'bg-yellow-500',
    running: 'bg-blue-500',
    starting: 'bg-blue-400',
    pending: 'bg-slate-300',
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <h3 className="mb-4 text-sm font-semibold text-slate-900">Instances by Status</h3>
      {total === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400">No instance data yet</div>
      ) : (
        <>
          <div className="mb-3 flex h-6 overflow-hidden rounded-full">
            {entries.map(([status, count]) => (
              <div
                key={status}
                className={`${colorMap[status] ?? 'bg-slate-300'}`}
                style={{ width: `${(count / total) * 100}%` }}
                title={`${status}: ${count}`}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            {entries.map(([status, count]) => (
              <div key={status} className="flex items-center gap-1.5 text-xs text-slate-600">
                <div className={`h-2.5 w-2.5 rounded-full ${colorMap[status] ?? 'bg-slate-300'}`} />
                <span className="capitalize">{status}</span>
                <span className="text-slate-400">
                  {count} ({Math.round((count / total) * 100)}%)
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TopAgentsTable({ agents, projectId }: {
  agents: { agentId: string; agentName: string; instanceCount: number; totalCost: number; avgCost: number; totalTokens: number }[];
  projectId: string;
}) {
  const navigate = useNavigate();

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <h3 className="mb-4 text-sm font-semibold text-slate-900">Top Agents</h3>
      {agents.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400">No agent data yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs text-slate-500">
                <th className="pb-2 pr-4 font-medium">Agent</th>
                <th className="pb-2 pr-4 font-medium">Instances</th>
                <th className="pb-2 pr-4 font-medium">Total Cost</th>
                <th className="pb-2 pr-4 font-medium">Avg Cost</th>
                <th className="pb-2 font-medium">Total Tokens</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr key={agent.agentId} className="border-b border-slate-100 last:border-0">
                  <td className="py-2.5 pr-4">
                    <button
                      onClick={() => navigate(`/projects/${projectId}`)}
                      className="font-medium text-slate-900 hover:text-blue-600"
                    >
                      {agent.agentName}
                    </button>
                  </td>
                  <td className="py-2.5 pr-4 text-slate-600">{agent.instanceCount}</td>
                  <td className="py-2.5 pr-4 text-slate-600">{formatCost(agent.totalCost)}</td>
                  <td className="py-2.5 pr-4 text-slate-600">{formatCost(agent.avgCost)}</td>
                  <td className="py-2.5 text-slate-600">{formatTokens(agent.totalTokens)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Analytics() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project } = useProject(projectId);
  const { data: stats, isLoading } = useProjectStats(projectId);

  return (
    <div className="p-8">
      <button
        onClick={() => navigate(`/projects/${projectId}`)}
        className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to {project?.name ?? 'project'}
      </button>

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
        <p className="mt-1 text-sm text-slate-500">
          Usage and cost analytics for {project?.name ?? 'project'}
        </p>
      </div>

      {isLoading ? (
        <div className="text-sm text-slate-400">Loading analytics...</div>
      ) : (
        <>
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={Server}
              label="Total Instances"
              value={stats?.totalInstances ?? 0}
            />
            <StatCard
              icon={Activity}
              label="Running Now"
              value={stats?.runningNow ?? 0}
            />
            <StatCard
              icon={Zap}
              label="Total Tokens"
              value={formatTokens(stats?.totalTokens ?? 0)}
            />
            <StatCard
              icon={DollarSign}
              label="Total Cost"
              value={formatCost(stats?.totalCostUsd ?? 0)}
            />
          </div>

          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <CostByDayChart data={stats?.costByDay ?? []} />
            <StatusBreakdown data={stats?.instancesByStatus ?? {}} />
          </div>

          <TopAgentsTable agents={stats?.topAgents ?? []} projectId={projectId!} />
        </>
      )}
    </div>
  );
}
